import { Box, Grid, Typography, Drawer } from '@mui/material';
import React, { useState, useMemo, useCallback } from 'react';
import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import { useSearchParams } from 'react-router-dom';
import { getSurveyDetailsColumns } from '../components/GridColumns';
import { QuickSearchToolbar } from '../components/QuickSearch';
import { SurveyFieldDetailsPage } from './SurveyFieldDetailsPage';
import { useSurveyDetails } from '../components/api/queries/hooks';
import { debounce } from '../utils/common';

export const SurveyDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get('id');

  const [displaySearchText, setDisplaySearchText] = useState('');
  const [apiSearchText, setApiSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [selectedFarmerName, setSelectedFarmerName] = useState(null);

  const { data: { data: surveyDetailsData = [], totalRecords = 0 } = {}, isLoading } =
    useSurveyDetails(surveyId, {
      pageSize,
      pageNumber: currentPage + 1,
      search: apiSearchText
    });

  const debouncedSetApiSearchText = useMemo(
    () => debounce((value) => setApiSearchText(value), 500),
    []
  );

  const requestSearch = useCallback(
    (searchValue) => {
      // Update display immediately
      setDisplaySearchText(searchValue);
      // Update API search with debounce
      debouncedSetApiSearchText(searchValue);
    },
    [debouncedSetApiSearchText]
  );

  const handleRowClick = (params) => {
    const { row } = params;
    setSelectedFieldId(row?.fieldId);
    setSelectedFarmerName(row?.farmerName);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedFieldId(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ margin: 1 }}>
          Survey Details
        </Typography>
      </Box>

      <Grid
        item
        style={{
          flexGrow: 1,
          height: '75vh',
          width: '100%'
        }}
      >
        <DataGrid
          components={{
            Toolbar: QuickSearchToolbar
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }
          }}
          componentsProps={{
            toolbar: {
              value: displaySearchText,
              onChange: (event) => requestSearch(event.target.value),
              clearSearch: () => requestSearch('')
            }
          }}
          loading={isLoading}
          rows={surveyDetailsData || []}
          getRowId={(row) => row?.fieldId}
          columns={getSurveyDetailsColumns()}
          pagination
          paginationMode="server"
          rowsPerPageOptions={[5, 10, 25, 100]}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            setCurrentPage(newPage);
          }}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setCurrentPage(0);
          }}
          rowCount={totalRecords}
          onRowClick={handleRowClick}
          disableSelectionOnClick
        />
      </Grid>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600, md: 600 } }
        }}
      >
        {drawerOpen && (
          <SurveyFieldDetailsPage
            fieldId={selectedFieldId}
            surveyId={surveyId}
            farmerName={selectedFarmerName}
            onClose={handleDrawerClose}
          />
        )}
      </Drawer>
    </Box>
  );
};
